/**
 * Daily News — a continuously-scrolling top-of-CRM ticker that runs the team's
 * ambient news on one line: the daily LERT scoring leaders, then the upcoming
 * conferences/shows, looping. Click the header chevron to expand the standings
 * into a ranked list with per-LERT breakdown. Visible to everyone on every CRM
 * tab; self-contained (fetches leaderboard + shows + the viewer's own name).
 *
 * Score = closed-won MTD × 15 + tasks closed today × 1  (see backend
 * tmd_leaderboard.py). Shows come from the company-wide /api/events/calendar
 * (fail-soft — absent in prod, so the shows section simply doesn't appear).
 */
import React, { useCallback, useEffect, useState } from "react";
import {
  makeStyles,
  tokens,
  Spinner,
  Button,
} from "@fluentui/react-components";
import {
  ChevronDown16Regular,
  ChevronRight16Regular,
  News16Regular,
} from "@fluentui/react-icons";

interface CrmApi {
  get: <T>(path: string) => Promise<T>;
  post: <T>(path: string, body?: unknown) => Promise<T>;
  isConfigured: () => boolean;
}

interface Show {
  id: string; name: string; city?: string; state?: string; booth?: string;
  show_start?: string; show_end?: string;
}

function fmtShowDates(start?: string, end?: string): string {
  if (!start) return "";
  const s = new Date(`${start}T00:00:00`);
  const e = end ? new Date(`${end}T00:00:00`) : null;
  const mon = (d: Date) => d.toLocaleString("en-US", { month: "short" });
  if (!e || start === end) return `${mon(s)} ${s.getDate()}`;
  if (s.getMonth() === e.getMonth()) return `${mon(s)} ${s.getDate()}–${e.getDate()}`;
  return `${mon(s)} ${s.getDate()} – ${mon(e)} ${e.getDate()}`;
}

interface LeaderboardEntry {
  name: string;
  user_id: string;
  score: number;
  breakdown: {
    closed_won_mtd: number;
    emails_today?: number;
    tasks_today: number;
    notes_today?: number;
    notes_today_scored?: number;
    calls_today?: number;
  };
}

interface LeaderboardResponse {
  entries: LeaderboardEntry[];
}

interface Props {
  crmApi: CrmApi;
  /** Current LERT name for row highlighting. Null/undefined for non-LERT viewers. */
  currentLertName?: string | null;
}

const useStyles = makeStyles({
  card: {
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground1,
    overflow: "hidden",
    // Stay visible as the briefing scrolls underneath, so the ticker behaves
    // like a real stock-ticker pinned to the top of the viewport.
    position: "sticky",
    top: 0,
    zIndex: 5,
    boxShadow: `0 2px 4px rgba(0, 0, 0, 0.08)`,
    // In a flex column parent, sticky children can collapse to 0 height
    // when other siblings grow; pin the size so the widget always shows.
    flexShrink: 0,
    alignSelf: "stretch",
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalM}`,
    cursor: "pointer",
    userSelect: "none",
    backgroundColor: tokens.colorNeutralBackground2,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    minHeight: "36px",
  },
  headerLabel: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalXS,
    flexShrink: 0,
  },
  headerTitle: {
    fontSize: "13px",
    fontWeight: 600,
  },
  // Ticker — the always-visible scrolling band replacing the old header hint.
  tickerWrap: {
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
    position: "relative",
    // Fade edges so items don't pop in/out hard.
    maskImage:
      "linear-gradient(to right, transparent 0, black 24px, black calc(100% - 24px), transparent 100%)",
    WebkitMaskImage:
      "linear-gradient(to right, transparent 0, black 24px, black calc(100% - 24px), transparent 100%)",
  },
  tickerTrack: {
    display: "inline-flex",
    alignItems: "center",
    whiteSpace: "nowrap",
    gap: tokens.spacingHorizontalL,
    animationName: {
      from: { transform: "translateX(0)" },
      to: { transform: "translateX(-50%)" },
    },
    animationDuration: "30s",
    animationTimingFunction: "linear",
    animationIterationCount: "infinite",
    willChange: "transform",
    ":hover": {
      animationPlayState: "paused",
    },
    "@media (prefers-reduced-motion: reduce)": {
      animationPlayState: "paused",
    },
  },
  tickerItem: {
    display: "inline-flex",
    alignItems: "baseline",
    gap: tokens.spacingHorizontalXS,
    fontSize: "13px",
    fontVariantNumeric: "tabular-nums",
    color: tokens.colorNeutralForeground1,
  },
  tickerItemMe: {
    color: tokens.colorBrandForeground1,
    fontWeight: 700,
  },
  tickerRank: {
    color: tokens.colorNeutralForeground3,
    fontWeight: 500,
  },
  tickerScore: {
    fontWeight: 600,
    marginLeft: "2px",
  },
  // Separator shown once between each loop of the 9-entry list.
  tickerSeparator: {
    display: "inline-flex",
    alignItems: "baseline",
    fontSize: "12px",
    letterSpacing: "1.5px",
    textTransform: "uppercase",
    color: tokens.colorNeutralForeground3,
    padding: `0 ${tokens.spacingHorizontalS}`,
  },
  tickerEmpty: {
    fontSize: "12px",
    color: tokens.colorNeutralForeground3,
    fontStyle: "italic",
    padding: `0 ${tokens.spacingHorizontalM}`,
  },
  body: {
    padding: `${tokens.spacingVerticalXS} 0`,
  },
  bodyMeta: {
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalM}`,
    fontSize: "11px",
    color: tokens.colorNeutralForeground3,
  },
  row: {
    display: "grid",
    gridTemplateColumns: "32px 1fr auto",
    alignItems: "center",
    gap: tokens.spacingHorizontalM,
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalM}`,
    fontSize: "14px",
  },
  rowHighlight: {
    backgroundColor: tokens.colorBrandBackground2,
  },
  rank: {
    color: tokens.colorNeutralForeground3,
    fontVariantNumeric: "tabular-nums",
    textAlign: "right",
    paddingRight: tokens.spacingHorizontalXS,
  },
  name: {
    fontWeight: 500,
  },
  score: {
    fontVariantNumeric: "tabular-nums",
    fontWeight: 600,
    fontSize: "15px",
  },
  breakdownLine: {
    gridColumn: "2 / 4",
    fontSize: "12px",
    color: tokens.colorNeutralForeground3,
  },
  loading: {
    padding: tokens.spacingVerticalM,
    display: "flex",
    justifyContent: "center",
  },
  error: {
    padding: tokens.spacingVerticalM,
    color: tokens.colorPaletteRedForeground1,
    fontSize: "13px",
  },
  refreshBtn: {
    minWidth: 0,
    flexShrink: 0,
  },
});

export const TmdLeaderboard: React.FC<Props> = ({ crmApi, currentLertName }) => {
  const styles = useStyles();
  const [expanded, setExpanded] = useState<boolean>(false);
  const [entries, setEntries] = useState<LeaderboardEntry[] | null>(null);
  const [shows, setShows] = useState<Show[]>([]);
  const [selfName, setSelfName] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [loadedAt, setLoadedAt] = useState<Date | null>(null);

  // Highlight name: prop wins (TMD-panel usage passes the impersonated LERT);
  // otherwise the viewer's own name resolved from /me (top-of-CRM usage).
  const highlightName = currentLertName || selfName;

  // crmApi from useCrmApiClient() is a fresh object on every parent render,
  // so we stash it in a ref to keep `load` stable and the effect from
  // firing on every parent re-render (e.g. when impersonation changes).
  const crmApiRef = React.useRef(crmApi);
  crmApiRef.current = crmApi;

  const load = useCallback(async () => {
    const api = crmApiRef.current;
    if (!api.isConfigured()) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<LeaderboardResponse>("/api/tmd/leaderboard");
      setEntries(res.entries || []);
      setLoadedAt(new Date());
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load leaderboard");
    } finally {
      setLoading(false);
    }
    // Upcoming shows — fail-soft (absent in prod / when not provisioned).
    try {
      const cal = await api.get<{ shows: Show[] }>("/api/events/calendar");
      setShows(cal.shows || []);
    } catch {
      setShows([]);
    }
    // Own name for ticker highlighting when no prop is passed.
    if (!currentLertName) {
      try {
        const me = await api.get<{ lert_name?: string; actor_name?: string }>("/api/tmd/me");
        setSelfName(me.lert_name || me.actor_name || null);
      } catch { /* highlighting is best-effort */ }
    }
  }, [currentLertName]);

  useEffect(() => {
    void load();
  }, [load]);

  const onToggle = useCallback(() => {
    setExpanded((v) => !v);
  }, []);

  const onRefresh = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      void load();
    },
    [load],
  );

  // Ticker renders two copies of the content (each a full section cycle) so the
  // CSS -50% translate loops seamlessly. Each half runs the scoring leaders,
  // then the upcoming shows, each behind its own labeled separator.
  const LEADERS_SEP = "◆ LERT DAILY SCORING LEADERS ◆";
  const SHOWS_SEP = "◆ UPCOMING CONFERENCES / SHOWS ◆";
  const renderTickerHalf = (copyIdx: number) => (
    <React.Fragment key={`half-${copyIdx}`}>
      <span className={styles.tickerSeparator}>{LEADERS_SEP}</span>
      {(entries || []).map((e, i) => {
        const isMe = !!highlightName && e.name === highlightName;
        return (
          <span
            key={`${e.user_id || e.name}-${copyIdx}-${i}`}
            className={`${styles.tickerItem} ${isMe ? styles.tickerItemMe : ""}`}
          >
            <span className={styles.tickerRank}>{i + 1}.</span>
            <span>{e.name}</span>
            <span className={styles.tickerScore}>{e.score}</span>
          </span>
        );
      })}
      {shows.length > 0 && (
        <>
          <span className={styles.tickerSeparator}>{SHOWS_SEP}</span>
          {shows.map((s, i) => (
            <span key={`show-${copyIdx}-${s.id}-${i}`} className={styles.tickerItem}>
              <span className={styles.tickerRank}>{i + 1}.</span>
              <span>{s.name}</span>
              {fmtShowDates(s.show_start, s.show_end) && (
                <span className={styles.tickerScore}>{fmtShowDates(s.show_start, s.show_end)}</span>
              )}
              {(s.city || s.state) && (
                <span className={styles.tickerRank}>{[s.city, s.state].filter(Boolean).join(", ")}</span>
              )}
            </span>
          ))}
        </>
      )}
    </React.Fragment>
  );

  const hasContent = (entries && entries.length > 0) || shows.length > 0;

  return (
    <div className={styles.card}>
      <div className={styles.header} onClick={onToggle}>
        <div className={styles.headerLabel}>
          {expanded ? <ChevronDown16Regular /> : <ChevronRight16Regular />}
          <News16Regular style={{ color: tokens.colorBrandForeground1 }} />
          <span className={styles.headerTitle}>Daily News</span>
        </div>

        <div className={styles.tickerWrap}>
          {hasContent ? (
            <div className={styles.tickerTrack}>
              {renderTickerHalf(0)}
              {renderTickerHalf(1)}
            </div>
          ) : (
            <span className={styles.tickerEmpty}>
              {loading ? "Loading..." : error ? error : "Nothing new today."}
            </span>
          )}
        </div>

        <Button
          size="small"
          appearance="subtle"
          disabled={loading}
          onClick={onRefresh}
          className={styles.refreshBtn}
        >
          {loading ? <Spinner size="tiny" /> : "Refresh"}
        </Button>
      </div>

      {expanded && (
        <div className={styles.body}>
          <div className={styles.bodyMeta}>
            Resets 12a ET
            {loadedAt && ` · updated ${loadedAt.toLocaleTimeString()}`}
          </div>
          {entries &&
            entries.map((e, i) => {
              const isMe =
                !!highlightName && e.name === highlightName;
              return (
                <React.Fragment key={e.user_id || `${e.name}-${i}`}>
                  <div
                    className={`${styles.row} ${isMe ? styles.rowHighlight : ""}`}
                  >
                    <span className={styles.rank}>{i + 1}.</span>
                    <span className={styles.name}>
                      {e.name}
                      {isMe ? " (you)" : ""}
                    </span>
                    <span className={styles.score}>{e.score}</span>
                    <span className={styles.breakdownLine}>
                      {e.breakdown.closed_won_mtd} closed-won MTD
                      {(e.breakdown.calls_today ?? 0) > 0 &&
                        ` · ${e.breakdown.calls_today} calls`}
                      {` · ${e.breakdown.emails_today ?? 0} emails`}
                      {` · ${e.breakdown.tasks_today} tasks`}
                      {` · ${e.breakdown.notes_today ?? 0} notes`}
                      {(e.breakdown.notes_today ?? 0) >
                        (e.breakdown.notes_today_scored ?? 0) &&
                        ` (capped at ${e.breakdown.notes_today_scored})`}
                    </span>
                  </div>
                </React.Fragment>
              );
            })}
        </div>
      )}
    </div>
  );
};

export default TmdLeaderboard;
